import { createSelector } from "reselect";
import {
  deliveryMethodsSelector,
  defaultDeliveryModeSelector,
  sessionTokenSelector,
  userOrdersDeliveryDetailsSelector
} from "ducks/selectors";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import {
  addressParams,
  deliverySlotParams,
  getChannelFromUrl,
  identityELOAParams,
  popStationParams
} from "utils/helpers";
import { ORDER_CHANNEL_MAPPING } from "utils/constants";
import head from "lodash/head";
import { personalDetailsSelector } from "./personalDetails";

const deliveryInfoDomain = state =>
  get(state, "form.DeliveryInfoForm.values", {});

// const postOfficeDeliverySlotsDomain = state =>
//   get(state, "api.postOfficeDeliverySlots.data");

// ***************************************************************
// ************** deliveryInfoForm selectors ******************
// ***************************************************************

export const deliveryInfoSelector = createSelector(
  deliveryInfoDomain,
  deliveryInfo => deliveryInfo
);

export const isDeliveryInfoAvailable = createSelector(
  deliveryInfoDomain,
  deliveryInfo => !isEmpty(get(deliveryInfo, "values", {}))
);

export const channelSelector = () =>
  get(ORDER_CHANNEL_MAPPING, getChannelFromUrl());

export const selectedDeliveryMethodSelector = createSelector(
  defaultDeliveryModeSelector,
  deliveryInfoSelector,
  (defaultDeliveryMethod, deliveryInfo) => {
    return get(deliveryInfo, "delivery_method", defaultDeliveryMethod);
  }
);

// ***************************************************************
// *************** deliveryInfoForm makeSelect *******************
// ***************************************************************

export const makeSelectDeliveryInfoForm = () => deliveryInfoSelector;

export const makeSelectDeliveryMethods = () => deliveryMethodsSelector;

export const makeSelectDeliveryMethod = () => selectedDeliveryMethodSelector;

export const makeSelectIsDeliveryInfoAvailable = () => isDeliveryInfoAvailable;

export const makeSelectScheduleDeliveryPayload = () =>
  createSelector(
    deliveryInfoSelector,
    sessionTokenSelector,
    userOrdersDeliveryDetailsSelector,
    (formValues, sessionToken, userOrder) => {
      if (isEmpty(formValues)) return {};
      const {
        alternate_number,
        order_type,
        delivery_type,
        token,
        fail_reason,
        receive_my_package,
        order_ref,
        delivery_method
      } = formValues;

      const { delivery_slot = {} } = deliverySlotParams(formValues);

      const payload = {
        order_type,
        delivery_type,
        token: token || sessionToken,
        order_ref,
        fail_reason: get(fail_reason, "value", ""),
        alternate_number:
          alternate_number ||
          get(formValues, "contact_number") ||
          get(head(userOrder), "contact_number"),
        ...(get(delivery_method, "action", "home") === "pop_station"
          ? popStationParams(formValues)
          : addressParams(formValues, "delivery")),
        ...delivery_slot,
        ...(receive_my_package ? identityELOAParams(formValues) : {})
      };

      return payload;
    }
  );

export const makeSelectDeliveryInfoFormInitialValues = () =>
  createSelector(
    personalDetailsSelector,
    selectedDeliveryMethodSelector,
    (personalDetailsForm, delivery_method) => {
      const {
        location,
        delivery_location_error,
        billing_location_error,
        delivery_show_all_address_fields,
        billing_show_all_address_fields,
        same_as_delivery_address,
        billing_location,
        billing_hse_blk_tower,
        billing_street_name,
        billing_city,
        billing_country,
        billing_zip_code,
        billing_unit_no,
        billing_building_name,
        delivery_location,
        delivery_hse_blk_tower,
        delivery_street_name,
        delivery_city,
        delivery_country,
        delivery_zip_code,
        delivery_unit_no,
        delivery_building_name
      } = personalDetailsForm || {};
      return {
        location: location || delivery_location || billing_location,
        delivery_location_error:
          delivery_location_error || billing_location_error,
        billing_location_error,
        delivery_show_all_address_fields,
        billing_show_all_address_fields,
        delivery_method,
        same_as_delivery_address,
        billing_location,
        billing_hse_blk_tower,
        billing_street_name,
        billing_city,
        billing_country,
        billing_zip_code,
        billing_unit_no,
        billing_building_name,
        delivery_location: delivery_location || billing_location,
        delivery_hse_blk_tower: delivery_hse_blk_tower || billing_hse_blk_tower,
        delivery_street_name: delivery_street_name || billing_street_name,
        delivery_city: delivery_city || billing_city,
        delivery_country: delivery_country || billing_country,
        delivery_zip_code: delivery_zip_code || billing_zip_code,
        delivery_unit_no: delivery_unit_no || billing_unit_no,
        delivery_building_name: delivery_building_name || billing_building_name
      };
    }
  );
